<template>
  <div class="asr">
    <div class="asr__card">
      <header class="asr__header">
        <i class="bi bi-mic-fill asr__header-icon" aria-hidden="true" />
        <div>
          <h2 class="asr__title">اتاق‌های فعال اسپیکینگ</h2>
          <p class="asr__subtitle">بساز و از دوستت بخواه وارد شود</p>
        </div>
      </header>

      <button
        type="button"
        class="asr__create"
        :disabled="creating"
        @click="createRoom"
      >
        <i class="bi bi-plus-lg" aria-hidden="true" />
        {{ creating ? "در حال ساخت…" : "ساخت اتاق جدید" }}
      </button>
      <p v-if="createError" class="asr__inline-error">{{ createError }}</p>

      <div v-if="replaceOpen" class="asr__confirm" role="dialog" aria-modal="true" aria-labelledby="asr-replace-title">
        <p id="asr-replace-title" class="asr__confirm-title">اتاق فعال دارید</p>
        <p class="asr__confirm-text">
          اتاق
          <span class="asr__confirm-code">{{ replaceCode }}</span>
          هنوز باز است. می‌خواهید آن را حذف کنید و یک اتاق جدید بسازید؟
        </p>
        <div class="asr__confirm-actions">
          <button type="button" class="asr__confirm-no" :disabled="creating" @click="cancelReplace">
            نه، بماند
          </button>
          <button type="button" class="asr__confirm-yes" :disabled="creating" @click="confirmReplace">
            {{ creating ? "در حال جایگزینی…" : "بله، حذف و ساخت" }}
          </button>
        </div>
      </div>

      <div v-if="removeOpen" class="asr__confirm" role="dialog" aria-modal="true" aria-labelledby="asr-remove-title">
        <p id="asr-remove-title" class="asr__confirm-title">حذف اتاق</p>
        <p class="asr__confirm-text">
          اتاق
          <span class="asr__confirm-code">{{ removeCode }}</span>
          حذف شود؟ نفر دوم دیگر نمی‌تواند وارد شود.
        </p>
        <div class="asr__confirm-actions">
          <button type="button" class="asr__confirm-no" :disabled="removing" @click="cancelRemove">
            انصراف
          </button>
          <button type="button" class="asr__confirm-yes" :disabled="removing" @click="confirmRemove">
            {{ removing ? "در حال حذف…" : "بله، حذف شود" }}
          </button>
        </div>
      </div>

      <div v-if="loading && !rooms.length" class="asr__status">در حال بارگذاری…</div>

      <p v-else-if="errorMessage" class="asr__inline-error">
        {{ errorMessage }}
        <button type="button" class="asr__retry" @click="load">تلاش مجدد</button>
      </p>

      <p v-else-if="!rooms.length" class="asr__empty">
        هنوز اتاقی فعال نیست. یک اتاق بساز تا اینجا دیده شود.
      </p>

      <ul v-else class="asr__list">
        <li
          v-for="room in rooms"
          :key="room.room_code"
          class="asr__row"
          :class="{ 'asr__row--open': canOpen(room) }"
          @click="openRoom(room)"
        >
          <div class="asr__row-main">
            <span class="asr__code">{{ room.room_code }}</span>
            <span class="asr__host">{{ hostLabel(room) }}</span>
            <span class="asr__meta">{{ statusLabel(room) }}</span>
          </div>
          <div class="asr__actions" @click.stop>
            <button
              v-if="isHost(room)"
              type="button"
              class="asr__remove"
              :disabled="removing"
              title="حذف اتاق"
              @click="askRemove(room)"
            >
              حذف
            </button>
            <button
              v-if="canOpen(room)"
              type="button"
              class="asr__join"
              title="باز کردن در تب جدید"
              @click="openRoom(room)"
            >
              ورود
            </button>
            <span v-else class="asr__full">پر</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import {
  createSpeakingRoom,
  deleteSpeakingRoom,
  listActiveSpeakingRooms,
  openSpeakingLobby,
  SpeakingRoomExistsError,
  type SpeakingRoom,
} from "@/api/speakingRooms";
import { useAuth } from "@/composables/useAuth";
import { handleError } from "@/services/errorHandler";

const POLL_MS = 8000;

export default {
  name: "ActiveSpeakingRooms",
  data() {
    return {
      rooms: [] as SpeakingRoom[],
      loading: true,
      creating: false,
      errorMessage: null as string | null,
      createError: null as string | null,
      replaceOpen: false,
      replaceCode: "",
      removeOpen: false,
      removeCode: "",
      removing: false,
      pollId: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    currentUserId(): string {
      const { user } = useAuth();
      return user.value?.id != null ? String(user.value.id) : "";
    },
  },
  created() {
    this.load();
    this.pollId = setInterval(() => {
      this.load({ silent: true });
    }, POLL_MS);
  },
  beforeUnmount() {
    if (this.pollId) clearInterval(this.pollId);
  },
  methods: {
    isHost(room: SpeakingRoom) {
      if (!this.currentUserId || !room.host_user_id) return false;
      return String(room.host_user_id) === this.currentUserId;
    },
    isMine(room: SpeakingRoom) {
      if (this.isHost(room)) return true;
      if (room.is_mine) return true;
      return false;
    },
    hostLabel(room: SpeakingRoom) {
      return (
        room.host_name ||
        room.examiner_name ||
        room.attendee_name ||
        "میزبان نامشخص"
      );
    },
    statusLabel(room: SpeakingRoom) {
      if (this.isMine(room) && !(room.examiner_taken && room.attendee_taken)) {
        return "اتاق شما · منتظر نفر دوم";
      }
      if (room.status === "in_progress") return "در حال تمرین";
      if (room.status === "round_break") return "استراحت راند";
      if (room.examiner_taken && room.attendee_taken) return "دو نفر در اتاق";
      if (room.examiner_taken || room.attendee_taken) return "منتظر نفر دوم";
      return "منتظر ورود";
    },
    async load({ silent = false }: { silent?: boolean } = {}) {
      if (!silent) this.loading = true;
      this.errorMessage = null;
      try {
        this.rooms = await listActiveSpeakingRooms();
      } catch (e) {
        if (!silent) {
          this.errorMessage = handleError(e, "ActiveSpeakingRooms.load");
          this.rooms = [];
        }
      } finally {
        this.loading = false;
      }
    },
    canOpen(room: SpeakingRoom) {
      return room.joinable || this.isHost(room);
    },
    joinRole(room: SpeakingRoom): "examiner" | "attendee" | undefined {
      if (room.open_role === "examiner" || room.open_role === "attendee") {
        return room.open_role;
      }
      if (!room.examiner_taken) return "examiner";
      if (!room.attendee_taken) return "attendee";
      return undefined;
    },
    openRoom(room: SpeakingRoom) {
      if (!this.canOpen(room)) return;
      openSpeakingLobby(room.room_code, this.joinRole(room));
    },
    askRemove(room: SpeakingRoom) {
      this.removeCode = room.room_code;
      this.removeOpen = true;
      this.createError = null;
    },
    cancelRemove() {
      this.removeOpen = false;
      this.removeCode = "";
    },
    async confirmRemove() {
      if (!this.removeCode) return;
      this.removing = true;
      this.createError = null;
      try {
        await deleteSpeakingRoom(this.removeCode);
        this.removeOpen = false;
        this.removeCode = "";
        await this.load({ silent: true });
      } catch (e) {
        this.createError = handleError(e, "ActiveSpeakingRooms.remove");
      } finally {
        this.removing = false;
      }
    },
    hostedRoom(): SpeakingRoom | undefined {
      return this.rooms.find((room) => this.isHost(room));
    },
    cancelReplace() {
      this.replaceOpen = false;
      this.replaceCode = "";
    },
    async confirmReplace() {
      await this.submitCreate(true);
    },
    async createRoom() {
      this.createError = null;
      const hosted = this.hostedRoom();
      if (hosted) {
        this.replaceCode = hosted.room_code;
        this.replaceOpen = true;
        return;
      }
      await this.submitCreate(false);
    },
    async submitCreate(replaceExisting: boolean) {
      this.creating = true;
      this.createError = null;
      try {
        const created = await createSpeakingRoom({ replaceExisting });
        this.replaceOpen = false;
        openSpeakingLobby(created.room_code, "examiner");
        await this.load({ silent: true });
      } catch (e) {
        if (e instanceof SpeakingRoomExistsError) {
          this.replaceCode = e.roomCode;
          this.replaceOpen = true;
        } else {
          this.createError = handleError(e, "ActiveSpeakingRooms.create");
          this.replaceOpen = false;
        }
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.asr {
  width: 100%;
  direction: rtl;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.asr__card {
  background: #ffffff;
  border: 1px solid #ece4d9;
  border-radius: 24px;
  padding: 22px 20px 18px;
  box-shadow: 0 4px 24px rgba(76, 55, 42, 0.07);
  color: #4c372a;
}

.asr__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.asr__header-icon {
  font-size: 1.3rem;
  color: #6d5ef8;
  margin-top: 2px;
}

.asr__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #4c372a;
  line-height: 1.2;
}

.asr__subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8c8176;
}

.asr__create {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px dashed #c4b5fd;
  border-radius: 12px;
  background: #fbf7fc;
  color: #6d5ef8;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.asr__create:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.asr__confirm {
  margin: 0 0 12px;
  padding: 12px 14px;
  background: #fbf7fc;
  border: 1px solid #c4b5fd;
  border-radius: 14px;
}

.asr__confirm-title {
  margin: 0 0 6px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #4c372a;
}

.asr__confirm-text {
  margin: 0 0 12px;
  font-size: 0.8rem;
  color: #8c8176;
  line-height: 1.7;
}

.asr__confirm-code {
  display: inline-block;
  direction: ltr;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #6d5ef8;
}

.asr__confirm-actions {
  display: flex;
  gap: 8px;
}

.asr__confirm-no,
.asr__confirm-yes {
  flex: 1;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.asr__confirm-no {
  border: 1px solid #ece4d9;
  background: #fff;
  color: #4c372a;
}

.asr__confirm-yes {
  border: none;
  background: #6d5ef8;
  color: #fff;
}

.asr__confirm-no:disabled,
.asr__confirm-yes:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.asr__status,
.asr__empty {
  margin: 0;
  padding: 12px 4px 6px;
  font-size: 0.82rem;
  color: #8c8176;
  line-height: 1.7;
  text-align: center;
}

.asr__inline-error {
  margin: 0 0 10px;
  font-size: 0.78rem;
  color: #b42318;
  line-height: 1.6;
}

.asr__retry {
  margin-right: 6px;
  border: none;
  background: none;
  color: #6d5ef8;
  font-size: inherit;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.asr__list {
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fffdf8;
  border: 1px solid #ece4d9;
  border-radius: 14px;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
}

.asr__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 12px;
  border-bottom: 1px solid #ece4d9;
}

.asr__row:last-child {
  border-bottom: none;
}

.asr__row--open {
  cursor: pointer;
}

.asr__row--open:hover {
  background: #fbf7fc;
}

.asr__row-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.asr__code {
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #6d5ef8;
  direction: ltr;
  text-align: right;
}

.asr__host {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4c372a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.asr__meta {
  font-size: 0.72rem;
  color: #8c8176;
}

.asr__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.asr__remove {
  padding: 6px 12px;
  border: 1px solid #f0c7c2;
  border-radius: 999px;
  background: #fff5f4;
  color: #b42318;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.asr__remove:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.asr__join {
  flex-shrink: 0;
  padding: 6px 14px;
  border: none;
  border-radius: 999px;
  background: #6d5ef8;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.asr__full {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #8c8176;
}
</style>
